import classNames from 'classnames';
import React, { ComponentProps, forwardRef, memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GerenciadorPrecos } from '../../../../../@types/GerenciadorPrecos';
import { RootState } from '../../../../../@types/RootState';
import {
  REMOVE_GERENCIADOR_EXPANDED_ROW_OPTION,
  RESET_GERENCIADOR_EXPANDED_ROW,
} from '../../../../../reducers/gerenciadorPrecos/expandedRowKey';
import { selectorOfferDataView } from '../../../../../reducers/gerenciadorPrecos/offerDataView';
import { useFarma } from '../../../../Administrador/Produtos/Components/SetupFarma/hooks/useFarma';
import { useVisibleExpandedRow } from '../../hooks/useVisibleExpandedRow';
import { ExpandedRowHeight } from '../../utils';
import { ExpandedRowCompetitors } from '../ExpandedRowCompetitors';
import { ExpandedRowCompetitorsOffer } from '../ExpandedRowCompetitorsOffer';
import ExpandedRowOffer from '../ExpandedRowOffer/ExpandedRowOffer';
import { ExpandedRowWholesale } from '../ExpandedRowWholesale';
import styles from './ExpandedRowTable.module.scss';
type RowData = GerenciadorPrecos.RowData;

export type ExpandedRowTableProps = Omit<ComponentProps<'table'>, 'children'> & {
  rowData?: RowData;
};

const ExpandedRowTable = memo(
  forwardRef<HTMLTableElement, ExpandedRowTableProps>(({ rowData, className, ...props }, ref) => {
    const dispatch = useDispatch();
    const { configFarma } = useFarma();

    const expandedRowKey = useVisibleExpandedRow();
    const { options } = expandedRowKey;
    const { autoExpandRows } = useSelector(selectorOfferDataView);
    const isOfferDataActive = useSelector((state: RootState) => state.gerenciadorPrecosReducer.quickActionsFilters.isOfferData.isActive);
    const isAutoExpandedOfferRow = Boolean(rowData?.offerDetails) && autoExpandRows && isOfferDataActive;
    const isExpanded = useMemo(() => isAutoExpandedOfferRow || options.length > 1, [isAutoExpandedOfferRow, options]);

    const isRowExpanded = useCallback((option: (typeof options)[number]) => options.includes(option), [options]);

    useEffect(() => {
      if (isAutoExpandedOfferRow) return;
      if (!isExpanded) dispatch(RESET_GERENCIADOR_EXPANDED_ROW());

      if (options.length === 2 && options.includes('HEADER') && options.includes('OFFERPRICE_COMPETITORS')) {
        dispatch(REMOVE_GERENCIADOR_EXPANDED_ROW_OPTION('OFFERPRICE_COMPETITORS'));
      }

      if (options.length === 3 && options.includes('HEADER') && options.includes('WHOLESALE') && options.includes('OFFERPRICE_COMPETITORS')) {
        dispatch(REMOVE_GERENCIADOR_EXPANDED_ROW_OPTION('OFFERPRICE_COMPETITORS'));
      }
    }, [dispatch, isAutoExpandedOfferRow, isExpanded, options]);

    const isHeaderAndCompetitors = useMemo(() => {
      if (isAutoExpandedOfferRow) return false;
      return options.length === 2 && options.includes('HEADER') && options.includes('COMPETITORS');
    }, [isAutoExpandedOfferRow, options]);

    if (!isExpanded || !rowData) return null;

    const isCurrentRowManuallyExpanded = expandedRowKey.key === rowData.productsToBePricedId;
    const showsWholesaleRow = !isAutoExpandedOfferRow && isRowExpanded('WHOLESALE');
    const showPmcColumn = Boolean(configFarma?.usePMCForIPAPricing);
    const shouldShowCompetitors = isRowExpanded('COMPETITORS') && (!isAutoExpandedOfferRow || isCurrentRowManuallyExpanded);
    const shouldShowOfferPriceCompetitors = isRowExpanded('OFFERPRICE_COMPETITORS') && (!isAutoExpandedOfferRow || isCurrentRowManuallyExpanded);

    return (
      <table
        {...props}
        data-table-row-is-expanded={isExpanded}
        ref={ref}
        className={classNames(styles['table'], className)}
        style={{ ['--_header-height' as string]: `${ExpandedRowHeight.getRowHeight('HEADER')}px` }}
      >
        {!isHeaderAndCompetitors && (
          <thead>
            <tr>
              {/* blank column to fix layout */}
              <th style={{ width: '60px' }}></th>
              <th>Outros preços</th>
              {/* store column blank to fix layout */}
              <th style={{ width: '80px' }}></th>
              {/* pmz column blank to fix layout */}
              <th style={{ width: '78px' }}></th>
              {/* objective margin column slot carries the minimum package quantity, wholesale row only */}
              <th style={{ width: '78px' }}>{showsWholesaleRow ? 'Emb..' : ''}</th>
              {/* pmc column blank to fix layout */}
              {showPmcColumn && <th style={{ width: '124px' }}></th>}
              <th style={{ width: '124px' }}>Preço vigente</th>
              <th style={{ width: '127px' }}>Preço concorrente</th>
              <th style={{ width: '132px' }}>Preço sugerido</th>
              <th style={{ width: '116px' }}>CPI</th>
              <th style={{ width: '116px' }}>Margem</th>
              <th style={{ width: '140px' }}>Desconto</th>
              {/* close button column blank to fix layout */}
              <th style={{ width: '26px' }}></th>
            </tr>
          </thead>
        )}
        <tbody>
          {isAutoExpandedOfferRow ? (
            <ExpandedRowOffer rowData={rowData} isAutoExpanded showPmcColumn={showPmcColumn} />
          ) : (
            <>
              {isRowExpanded('WHOLESALE') && <ExpandedRowWholesale rowData={rowData} showPmcColumn={showPmcColumn} />}
              {rowData?.offerDetails && <ExpandedRowOffer rowData={rowData} showPmcColumn={showPmcColumn} />}
            </>
          )}
          {shouldShowCompetitors && (
            <tr>
              <td colSpan={showPmcColumn ? 12 : 11}>
                <ExpandedRowCompetitors rowData={rowData} />
              </td>
            </tr>
          )}
          {shouldShowOfferPriceCompetitors && (
            <tr>
              <td colSpan={showPmcColumn ? 12 : 11}>
                <ExpandedRowCompetitorsOffer rowData={rowData} />
              </td>
            </tr>
          )}
        </tbody>
      </table>
    );
  }),
);

ExpandedRowTable.displayName = 'ExpandedRowTable';

export { ExpandedRowTable };
